import type { DateRange, Matcher } from "react-day-picker"
import { ru } from "date-fns/locale"
import type { ReactNode } from "react"

import { cn } from "@/lib/utils"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover"
import { useState } from "react"

type SharedProps = {
  placeholder?: string
  className?: string
  id?: string
  disabled?: boolean
  disabledDays?: Matcher | Matcher[]
  size?: "sm" | "default"
  /**
   * Trigger-button style:
   *   • "cell" — узкая h-6 ячейка (по умолчанию; для таблиц и rail).
   *   • "chip" — pill-чип h-7 rounded-full border (для create-форм).
   */
  triggerVariant?: "cell" | "chip"
  /** Иконка слева (только в chip-варианте). */
  leadingIcon?: ReactNode
}

type SingleDatePickerProps = SharedProps & {
  mode: "single"
  value?: Date
  onChange?: (value: Date | undefined) => void
}

type RangeDatePickerProps = SharedProps & {
  mode?: "range"
  value?: DateRange
  onChange?: (value: DateRange | undefined) => void
}

type DatePickerProps = SingleDatePickerProps | RangeDatePickerProps

function formatDate(value: Date) {
  const day = String(value.getDate()).padStart(2, "0")
  const month = String(value.getMonth() + 1).padStart(2, "0")
  const year = value.getFullYear()
  return `${day}.${month}.${year}`
}

function formatSingleLabel(value: Date | undefined) {
  if (!value) return null
  return formatDate(value)
}

function formatRangeLabel(value: DateRange | undefined) {
  if (!value?.from) return null
  if (!value.to) return formatDate(value.from)
  return `${formatDate(value.from)} - ${formatDate(value.to)}`
}

export function DatePicker(props: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const label =
    props.mode === "single"
      ? formatSingleLabel(props.value)
      : formatRangeLabel(props.value)

  const hasValue = label !== null
  const isChip = props.triggerVariant === "chip"
  // `size` retained for API compatibility — currently always renders at "default" h-6
  void props.size

  const trigger = (
    <button
      type="button"
      id={props.id}
      disabled={props.disabled}
      onClick={() => !props.disabled && setOpen((v) => !v)}
      aria-expanded={open}
      className={cn(
        isChip
          ? "group/chip inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border border-border/80 bg-background px-2.5 text-left text-[12.5px] leading-none font-normal text-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 data-[open=true]:bg-muted/60 data-[open=true]:text-foreground"
          : "flex h-6 w-full items-center gap-1.5 rounded px-1.5 text-left text-[12.5px] leading-none font-normal transition-colors hover:bg-muted/70 focus:bg-muted/70 focus:outline-none data-[open=true]:bg-muted/70",
        !hasValue && !isChip && "text-muted-foreground/40",
        !hasValue && isChip && "text-muted-foreground/80",
        props.disabled && "pointer-events-none opacity-100",
        props.className
      )}
      data-open={open}
    >
      {isChip && props.leadingIcon ? (
        <span className="shrink-0 text-muted-foreground [&_svg]:size-3.5">
          {props.leadingIcon}
        </span>
      ) : null}
      <span className="truncate flex-1">{label ?? props.placeholder ?? ""}</span>
    </button>
  )

  if (props.disabled) {
    return trigger
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>{trigger}</PopoverAnchor>
      <PopoverContent className="w-auto p-0" align="start">
        {props.mode === "single" ? (
          <Calendar
            mode="single"
            defaultMonth={props.value}
            selected={props.value}
            onSelect={(d) => {
              props.onChange?.(d ?? undefined)
              if (d) setOpen(false)
            }}
            disabled={props.disabledDays}
            locale={ru}
          />
        ) : (
          <Calendar
            mode="range"
            defaultMonth={props.value?.from}
            selected={props.value}
            onSelect={props.onChange}
            disabled={props.disabledDays}
            numberOfMonths={2}
            locale={ru}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}

type DateRangePickerProps = SharedProps & {
  value?: DateRange
  onChange?: (value: DateRange | undefined) => void
}

export function DateRangePicker(props: DateRangePickerProps) {
  return <DatePicker mode="range" {...props} />
}
